import type { CSSProperties } from "react";
import type { GuessRow, KeyState } from "../wordle/keystate";
import { FlapTile, type FlapMode } from "./FlapTile";

/** Gap between reveal flips; the last flip plus its settle closes the reveal. */
export const FLAP_STAGGER_MS = 250;
export const FLAP_REVEAL_TOTAL_MS = 4 * FLAP_STAGGER_MS + 450;
/** Diagonal step of the win wave. */
const WAVE_STEP_MS = 45;
const ROWS = 6;

/** Width-bound on wide screens, height-bound on short ones — shared with the strip above the board. */
export function flapBoardWidth(cols: number, reservedPx: number): string {
  return `min(364px, calc((var(--tg-viewport-stable-height, 100vh) - ${reservedPx}px) * ${cols} / ${ROWS} + 14px))`;
}

interface FlapGridProps {
  guesses: GuessRow[];
  current: string;
  /** Row whose reveal is playing (the guess just submitted). */
  revealRow: number | null;
  shakeRow: number | null;
  /** Bump once a winning reveal has landed: every module flips in a diagonal wave. */
  wave: number;
  winRow: number | null;
  finished: boolean;
  cols?: number;
  /** Vertical px reserved for everything that isn't the grid (height-bound formula). */
  reservedPx?: number;
  alphabet?: string;
  /** A reveal flip landed — the haptic tick. */
  onLand?: () => void;
}

/**
 * Wordle as a departures board: the same 6×N grid as `Grid`, but every cell is
 * a split-flap module. Keys stay stable per cell (no remounts) so each module
 * animates from what it showed to what it should show.
 */
export function FlapGrid({
  guesses,
  current,
  revealRow,
  shakeRow,
  wave,
  winRow,
  finished,
  cols = 5,
  reservedPx = 400,
  alphabet,
  onLand,
}: FlapGridProps) {
  return (
    <div
      className="flap-board mx-auto grid w-full grid-rows-6 gap-[5px] p-[7px]"
      style={{ maxWidth: flapBoardWidth(cols, reservedPx) }}
    >
      {Array.from({ length: ROWS }, (_, r) => {
        const g = guesses[r];
        const isActive = r === guesses.length && !finished;
        const letters = g
          ? g.word.split("")
          : isActive
            ? current.padEnd(cols, " ").split("").map((ch) => ch.trim())
            : Array.from({ length: cols }, () => "");
        const marks = g ? (g.marks.split("") as KeyState[]) : [];
        const mode: FlapMode = revealRow === r ? "flip" : isActive ? "type" : "none";
        const rowClass = [
          "grid gap-[5px]",
          shakeRow === r ? "row-shake flap-row-rattle" : "",
          winRow === r ? "flap-win" : "",
        ].join(" ");
        const rowStyle: CSSProperties & Record<string, string> = {
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          "--glow-delay": `${(r + cols) * WAVE_STEP_MS + 260}ms`,
        };
        return (
          <div key={r} className={rowClass} style={rowStyle}>
            {letters.map((ch, c) => (
              <FlapTile
                key={c}
                face={{ ch, s: marks[c] ?? null }}
                mode={mode}
                delay={mode === "flip" ? c * FLAP_STAGGER_MS : 0}
                wave={wave}
                waveDelay={(r + c) * WAVE_STEP_MS}
                {...(alphabet ? { alphabet } : {})}
                {...(onLand ? { onLand } : {})}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
